import { TZDate } from '@date-fns/tz';
import { format, parseISO } from 'date-fns';

import { useAuthStore } from '@/stores/auth';
import { useSettingsStore, type DateFormat } from '@/stores/settings';

export type TimeFormat = '12h' | '24h';
export type DateInput = string | Date | null | undefined;

export function getUserTimezone(): string {
  const authStore = useAuthStore();
  return authStore.currentUser?.timezone || 'UTC';
}

export function getUserTimeFormat(): TimeFormat {
  const settingsStore = useSettingsStore();
  return settingsStore.activeTimeFormat;
}

const DATE_FNS_PATTERNS: Record<DateFormat, string> = {
  iso: 'yyyy-MM-dd',
  us: 'MM/dd/yyyy',
  eu: 'dd/MM/yyyy',
  short: 'M/d/yy',
  medium: 'PP',
  long: 'PPP',
  full: 'PPPP',
};

const PRIME_DATE_MASKS: Record<DateFormat, string> = {
  iso: 'yy-mm-dd',
  us: 'mm/dd/yy',
  eu: 'dd/mm/yy',
  short: 'm/d/y',
  medium: 'M d, yy',
  long: 'MM d, yy',
  full: 'DD, MM d, yy',
};

export const PICKER_MONTH_MASK = 'MM yy';
export const PICKER_MONTH_COMPACT_MASK = 'yy-mm';

const TIME_PATTERNS: Record<TimeFormat, string> = {
  '12h': 'h:mm a',
  '24h': 'HH:mm',
};

const TIME_WITH_SECONDS_PATTERNS: Record<TimeFormat, string> = {
  '12h': 'h:mm:ss a',
  '24h': 'HH:mm:ss',
};

const DEFAULT_DATE_FORMAT: DateFormat = 'medium';
const DEFAULT_TIME_FORMAT: TimeFormat = '24h';

function dateFnsPattern(preference: DateFormat): string {
  return DATE_FNS_PATTERNS[preference] ?? DATE_FNS_PATTERNS[DEFAULT_DATE_FORMAT];
}

function timePattern(preference: TimeFormat): string {
  return TIME_PATTERNS[preference] ?? TIME_PATTERNS[DEFAULT_TIME_FORMAT];
}

function timeWithSecondsPattern(preference: TimeFormat): string {
  return TIME_WITH_SECONDS_PATTERNS[preference] ?? TIME_WITH_SECONDS_PATTERNS[DEFAULT_TIME_FORMAT];
}

export function primeDateFormat(): string {
  const settingsStore = useSettingsStore();
  const preference = settingsStore.activeDateFormat;

  return PRIME_DATE_MASKS[preference] ?? PRIME_DATE_MASKS[DEFAULT_DATE_FORMAT];
}

export function primeHourFormat(): '12' | '24' {
  return getUserTimeFormat() === '12h' ? '12' : '24';
}

export function formatDate(date: DateInput, formatString?: string, timezone?: string, timeFormat?: TimeFormat): string {
  if (!date) {
    return '';
  }

  const tz = timezone || getUserTimezone();
  const tf = timeFormat || getUserTimeFormat();
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  const zonedDate = new TZDate(dateObj, tz);

  let finalFormat = formatString;
  if (!finalFormat) {
    const settingsStore = useSettingsStore();
    finalFormat = `${dateFnsPattern(settingsStore.activeDateFormat)} ${timeWithSecondsPattern(tf)}`;
  }

  return format(zonedDate, finalFormat);
}

export function formatDateTime(date: DateInput, timezone?: string, timeFormat?: TimeFormat): string {
  const settingsStore = useSettingsStore();
  const tf = timeFormat || getUserTimeFormat();
  const dateFormatPref = settingsStore.activeDateFormat;

  const formatString = `${dateFnsPattern(dateFormatPref)} ${timePattern(tf)}`;
  return formatDate(date, formatString, timezone, tf);
}

export function formatDateTimeWithSeconds(date: DateInput, timezone?: string, timeFormat?: TimeFormat): string {
  const settingsStore = useSettingsStore();
  const tf = timeFormat || getUserTimeFormat();
  const dateFormatPref = settingsStore.activeDateFormat;

  const formatString = `${dateFnsPattern(dateFormatPref)} ${timeWithSecondsPattern(tf)}`;
  return formatDate(date, formatString, timezone, tf);
}

export function formatDateOnly(date: DateInput, timezone?: string): string {
  const settingsStore = useSettingsStore();
  const dateFormatPref = settingsStore.activeDateFormat;

  const formatString = dateFnsPattern(dateFormatPref);
  return formatDate(date, formatString, timezone);
}

export function formatTimeOnly(date: DateInput, timezone?: string, timeFormat?: TimeFormat): string {
  const tf = timeFormat || getUserTimeFormat();
  return formatDate(date, timePattern(tf), timezone, tf);
}

export function formatTimeWithSeconds(date: DateInput, timezone?: string, timeFormat?: TimeFormat): string {
  const tf = timeFormat || getUserTimeFormat();
  return formatDate(date, timeWithSecondsPattern(tf), timezone, tf);
}

function formatWallClock(value: string, formatter: (date: DateInput, timezone?: string) => string): string {
  if (!value) {
    return '';
  }

  const iso = value.includes('T') ? value : value.replace(' ', 'T');
  const withTime = iso.includes(':') ? iso : `${iso}T00:00:00`;
  const utc = withTime.endsWith('Z') ? withTime : `${withTime}Z`;

  return formatter(utc, 'UTC');
}

export function formatCalendarDate(date: string): string {
  return formatWallClock(date, formatDateOnly);
}

export function formatWallClockDateTime(date: string): string {
  return formatWallClock(date, formatDateTime);
}

export function formatChartDate(date: string): string {
  return formatWallClock(date, value => formatDate(value, 'MMM d', 'UTC'));
}

export function formatChartHour(hour: string): string {
  const twelveHour = getUserTimeFormat() === '12h';

  return formatWallClock(hour, value =>
    twelveHour ? formatDate(value, 'ha', 'UTC') : `${formatDate(value, 'HH', 'UTC')}h`,
  );
}

export function formatDateRangeLabel(from: DateInput, to: DateInput): string {
  return `${formatDate(from, 'MMM d')} – ${formatDate(to, 'MMM d, yyyy')}`;
}

export function toUserTimezone(date: DateInput, timezone?: string): TZDate | null {
  if (!date) {
    return null;
  }

  const tz = timezone || getUserTimezone();
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  return new TZDate(dateObj, tz);
}

export function now(timezone?: string): TZDate {
  const tz = timezone || getUserTimezone();
  return new TZDate(tz);
}

export function nowInUserTimezone(timezone?: string): TZDate {
  const tz = timezone || getUserTimezone();
  return new TZDate(new Date(), tz);
}

export function startOfDayInUserTz(date: Date, timezone?: string): TZDate {
  const tz = timezone || getUserTimezone();
  return new TZDate(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0, tz);
}

export function endOfDayInUserTz(date: Date, timezone?: string): TZDate {
  const tz = timezone || getUserTimezone();
  return new TZDate(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999, tz);
}

/**
 * Format pattern reference:
 *
 * Common formats (24-hour):
 * - 'yyyy-MM-dd' => 2024-01-15
 * - 'yyyy-MM-dd HH:mm:ss' => 2024-01-15 18:30:00
 * - 'PP' => Jan 15, 2024
 * - 'PP HH:mm' => Jan 15, 2024 18:30
 * - 'HH:mm' => 18:30
 * - 'HH:mm:ss' => 18:30:45
 *
 * Common formats (12-hour):
 * - 'yyyy-MM-dd hh:mm:ss a' => 2024-01-15 06:30:00 PM
 * - 'PPp' => Jan 15, 2024, 6:30 PM
 * - 'h:mm a' => 6:30 PM
 * - 'hh:mm:ss a' => 06:30:45 PM
 *
 * Pattern elements:
 * - yyyy: 4-digit year
 * - MM: 2-digit month (01-12)
 * - dd: 2-digit day (01-31)
 * - HH: 24-hour format hour (00-23)
 * - hh: 12-hour format hour (01-12)
 * - h: 12-hour format hour without leading zero (1-12)
 * - mm: minutes (00-59)
 * - ss: seconds (00-59)
 * - a: AM/PM marker
 * - P: Localized date
 * - p: Localized time
 *
 * Time format preference:
 * - The getUserTimeFormat() function automatically detects the employee's preferred format
 * - Currently based on browser locale (en-US/en-CA use 12h, others use 24h)
 * - Can be overridden by passing timeFormat parameter to formatting functions
 *
 * See: https://date-fns.org/docs/format
 */
